"use client";

import { useEffect, useState, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Percent,
  Leaf,
  Flame,
  Cookie,
  Droplets,
  Wind,
  FlaskConical,
  Hand,
  ShoppingBag,
  Shirt,
  Pill,
  ArrowLeft,
  ChevronRight,
  Tag,
} from "lucide-react";
import { getProducts, type ProductDisplay } from "@/lib/api/dutchie";
import { areTagsEquivalent } from "@/lib/format-text";

/**
 * The previous version of this page used hardcoded JPGs in
 * /public/images/categories/ which had drifted out of sync with their
 * filenames, flower.jpg was a vape image, topicals.jpg was BLISS
 * pills, etc. The replacement (icon-only) felt sterile.
 *
 * This version pulls a real, in-stock product image FROM each category
 * via the live Dutchie data, so each card visually represents what's
 * actually in that category right now. The gradient overlay + icon
 * badge keep the branding consistent.
 *
 * Categories that don't have any in-stock products gracefully fall
 * back to the gradient-only design while live data is loading or empty.
 */

interface CategoryInfo {
  slug: string;
  // The category key Dutchie uses (e.g., "FLOWER", "PRE_ROLLS").
  // Used to match products to this category. When omitted (Specials,
  // Shop by Brand), no live image lookup happens.
  dutchieCategory?: string;
  // For virtual categories that don't exist in Dutchie's taxonomy
  // (e.g., Orals = EDIBLES → CAPSULES_TABLET | MINTS), match by
  // subcategory under a parent. Takes precedence over dutchieCategory.
  dutchieSubcategoryMatch?: { parentCategory: string; subcategoryRegex: RegExp };
  name: string;
  description: string;
  icon: any;
  color: string;
  isSpecial?: boolean;
  badgeText?: string;
}

const categories: CategoryInfo[] = [
  {
    slug: "/specials",
    name: "Specials & Deals",
    description: "Save big with our current promotions and discounts.",
    icon: Percent,
    color: "from-primary_green to-emerald-600",
    isSpecial: true,
  },
  {
    slug: "/shop/brand",
    name: "Shop by Brand",
    description: "Browse products from your favorite cannabis brands.",
    icon: Tag,
    color: "from-amber-500 to-orange-600",
    isSpecial: true,
    badgeText: "BRANDS",
  },
  {
    slug: "flower",
    dutchieCategory: "flower",
    name: "Flower",
    description: "Premium cannabis buds in various strains and potencies.",
    icon: Leaf,
    color: "from-green-600 to-green-700",
  },
  {
    slug: "pre-rolls",
    dutchieCategory: "pre-rolls",
    name: "Pre-Rolls",
    description: "Ready-to-smoke joints made from quality flower.",
    icon: Flame,
    color: "from-orange-500 to-orange-600",
  },
  {
    slug: "edibles",
    dutchieCategory: "edibles",
    name: "Edibles",
    description: "Gummies, chocolates, and beverages for long-lasting effects.",
    icon: Cookie,
    color: "from-pink-500 to-rose-600",
  },
  {
    slug: "concentrates",
    dutchieCategory: "concentrates",
    name: "Concentrates",
    description: "Potent extracts like wax, shatter, and live resin.",
    icon: Droplets,
    color: "from-amber-500 to-amber-600",
  },
  {
    slug: "vaporizers",
    dutchieCategory: "vaporizers",
    name: "Vaporizers",
    description: "Cartridges and disposables for quick, discreet sessions.",
    icon: Wind,
    color: "from-sky-500 to-blue-600",
  },
  {
    slug: "tinctures",
    dutchieCategory: "tinctures",
    name: "Tinctures",
    description: "Sublingual drops for precise dosing and fast onset.",
    icon: FlaskConical,
    color: "from-violet-500 to-purple-600",
  },
  {
    slug: "topicals",
    dutchieCategory: "topicals",
    name: "Topicals",
    description: "Creams and balms for localized relief.",
    icon: Hand,
    color: "from-teal-500 to-teal-600",
  },
  {
    slug: "accessories",
    dutchieCategory: "accessories",
    name: "Accessories",
    description: "Papers, grinders, and glassware for your sessions.",
    icon: ShoppingBag,
    color: "from-slate-500 to-slate-600",
  },
  {
    slug: "apparel",
    dutchieCategory: "apparel",
    name: "Apparel",
    description: "Rep the 716 brand with our clothing line.",
    icon: Shirt,
    color: "from-indigo-500 to-indigo-600",
  },
  {
    slug: "orals",
    // Dutchie has no "ORALS" category, it lives under EDIBLES with
    // subcategory CAPSULES_TABLET or MINTS. Match those instead.
    dutchieSubcategoryMatch: {
      parentCategory: "edibles",
      subcategoryRegex: /capsul|tablet|mint|pill|oral/i,
    },
    name: "Orals",
    description: "Capsules, tablets and mints for controlled dosing.",
    icon: Pill,
    color: "from-cyan-500 to-cyan-600",
  },
];

/**
 * Generic-looking image URLs / patterns to skip when picking a
 * representative image (placeholder pixels, blank Dutchie defaults,
 * brand logos that show up for many products).
 */
function isUsableImage(url: string | undefined | null): url is string {
  if (!url) return false;
  if (url.includes("placeholder")) return false;
  if (url.endsWith(".svg")) return false; // logos are usually svg
  return true;
}

export default function CategoryIndexPage() {
  const [products, setProducts] = useState<ProductDisplay[]>([]);
  const [productsLoaded, setProductsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const p = await getProducts();
        if (!cancelled) {
          setProducts(p);
          setProductsLoaded(true);
        }
      } catch (e) {
        console.error("Failed to load products for category page:", e);
        if (!cancelled) setProductsLoaded(true);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build a category-slug → image URL map by picking the first in-stock
  // product whose category matches AND has a real product image.
  // areTagsEquivalent handles aliases (tinctures ↔ drops/oils, vapes ↔
  // vaporizers) the same way the category filter does.
  const categoryImages = useMemo(() => {
    const map: Record<string, string> = {};
    if (!productsLoaded || products.length === 0) return map;

    for (const cat of categories) {
      if (map[cat.slug]) continue;

      // Two matching strategies:
      //   1. dutchieSubcategoryMatch (virtual categories like Orals)
      //   2. dutchieCategory (regular categories)
      let candidates: ProductDisplay[] = [];

      if (cat.dutchieSubcategoryMatch) {
        const { parentCategory, subcategoryRegex } = cat.dutchieSubcategoryMatch;
        const parentLower = parentCategory.toLowerCase();
        candidates = products.filter((p) => {
          const pc = (p.category || "").toLowerCase().replace(/_/g, "-");
          const ps = (p.subcategory || "").toLowerCase();
          return (pc === parentLower || areTagsEquivalent(pc, parentLower))
            && subcategoryRegex.test(ps);
        });
      } else if (cat.dutchieCategory) {
        const target = cat.dutchieCategory.toLowerCase();
        candidates = products.filter((p) => {
          const pc = (p.category || "").toLowerCase().replace(/_/g, "-");
          return pc === target || areTagsEquivalent(pc, target);
        });
      } else {
        continue; // Specials / Shop-by-Brand stay gradient-only.
      }

      // Prefer in-stock products with usable images; fall back to any image.
      const pick =
        candidates.find((p) => p.inStock && isUsableImage(p.image)) ||
        candidates.find((p) => isUsableImage(p.image));

      if (pick && pick.image) map[cat.slug] = pick.image;
    }
    return map;
  }, [products, productsLoaded]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <Link
            href="/shop"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-primary_green mb-4 text-sm font-medium transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Shop
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">All Categories</h1>
          <p className="text-gray-500 mt-1">
            Browse our full selection of cannabis products
          </p>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const photo = categoryImages[cat.slug];

            return (
              <Link
                key={cat.slug}
                href={cat.isSpecial ? cat.slug : `/shop/category/${cat.slug}`}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-transparent"
              >
                {/* Hero area */}
                <div className={`relative h-36 sm:h-40 overflow-hidden bg-gradient-to-br ${cat.color}`}>
                  {/* Real product photo background, only when we found
                      one from THIS category. Falls back to gradient-only
                      while loading or for Specials/Brand cards.        */}
                  {photo && (
                    <Image
                      src={photo}
                      alt=""
                      fill
                      className="object-cover object-center group-hover:scale-110 transition-transform duration-500"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                      unoptimized
                    />
                  )}

                  {/* Color gradient overlay (lighter when a photo exists,
                      heavier when not so the icon still pops)            */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${cat.color} ${photo ? "opacity-65 group-hover:opacity-70" : "opacity-100"} transition-opacity`}
                  />

                  {/* Decorative circles (only when no photo to avoid clutter) */}
                  {!photo && (
                    <>
                      <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full" />
                      <div className="absolute -bottom-10 -left-10 w-28 h-28 bg-white/10 rounded-full" />
                    </>
                  )}

                  {/* Icon badge (top-left) */}
                  <div className="absolute top-3 left-3 w-10 h-10 rounded-xl bg-white/25 backdrop-blur-sm flex items-center justify-center shadow-sm z-10">
                    <Icon className="h-5 w-5 text-white" />
                  </div>

                  {/* Special Badge (top-right) */}
                  {cat.isSpecial && (
                    <div
                      className={`absolute top-3 right-3 bg-white px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg z-10 ${cat.badgeText === "BRANDS" ? "text-amber-600" : "text-primary_green"}`}
                    >
                      {cat.badgeText === "BRANDS" ? (
                        <Tag className="h-3 w-3" />
                      ) : (
                        <Percent className="h-3 w-3" />
                      )}
                      {cat.badgeText || "DEALS"}
                    </div>
                  )}

                  {/* Title at bottom */}
                  <h2 className="absolute bottom-3 left-4 text-white text-xl font-bold drop-shadow-lg z-10">
                    {cat.name}
                  </h2>
                </div>

                {/* Description */}
                <div className="p-4 flex items-center justify-between">
                  <p className="text-sm text-gray-600 line-clamp-2 flex-1">
                    {cat.description}
                  </p>
                  <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-primary_green group-hover:translate-x-1 transition-all flex-shrink-0 ml-2" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* View All Products Button */}
        <div className="mt-8 text-center">
          <Link
            href="/shop/all"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary_green hover:bg-primary_green/90 text-white font-semibold rounded-full shadow-md hover:shadow-lg transition-all"
          >
            View All Products
            <ChevronRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
